// ---------------------------------------------------------------------------
// The month grid, shared by both calendars.
// ---------------------------------------------------------------------------
// There are two: a PROJECT calendar (one team's board on its key date) and MY
// calendar (everything assigned to you, across every project). They differ only
// in where the tasks come from, which date each task is filed under, and what
// happens on a drop — so those are the props, and everything else is here once.
//
// Extracted rather than copied. A second hand-rolled month grid would drift from
// this one the first time either was touched, and the drift would be silent:
// both would still render twelve months of squares.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Ban, Plus, Star } from 'lucide-react';
import type { TaskListItem } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { dueBucket } from '@/features/tasks/grouping';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CLOSED = new Set(['1_done', '1_canceled']);

export function ymd(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function chipTone(t: TaskListItem, dateStr: string | null): string {
  if (CLOSED.has(t.state)) return 'bg-hover text-faint line-through';
  if (t.state === '04_waiting_normal') return 'bg-danger-soft text-danger';
  const bucket = dueBucket(dateStr);
  if (bucket === 'overdue') return 'bg-danger/20 text-danger';
  if (bucket === 'today') return 'bg-warn-soft text-warn';
  return 'bg-brand-soft text-ink';
}

/** Urgency as TEXT colour only — used in dot mode, where the fill is the
 *  project's colour and cannot also carry "this is late". */
function chipText(t: TaskListItem, dateStr: string | null): string {
  if (CLOSED.has(t.state)) return 'text-faint line-through';
  if (t.state === '04_waiting_normal') return 'text-danger';
  const bucket = dueBucket(dateStr);
  if (bucket === 'overdue') return 'text-danger font-medium';
  if (bucket === 'today') return 'text-warn';
  return 'text-ink';
}

function Chip({
  task, dateStr, meta, color,
}: { task: TaskListItem; dateStr: string | null; meta?: string | null; color?: string | null }) {
  const open = useTaskDetailStore((s) => s.open);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id, data: { task } });

  // TWO MODES, and the difference is which question the colour answers.
  //   Project calendar — one project, so colour is free to mean URGENCY (filled
  //     chip: red overdue, amber today).
  //   My calendar — many projects overlaid, so colour has to mean WHICH PROJECT,
  //     exactly as Google Calendar does it: a coloured dot, then the text. Urgency
  //     moves to the text colour instead of being lost.
  const dotMode = Boolean(color);
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => open(task.id)}
      className={`flex w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-left text-[11px] transition hover:brightness-110 ${
        dotMode ? `hover:bg-hover ${chipText(task, dateStr)}` : chipTone(task, dateStr)
      } ${isDragging ? 'opacity-40' : ''}`}
      title={meta ? `${task.name} — ${meta}` : task.name}
    >
      {dotMode && (
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: color as string }}
        />
      )}
      <span className="truncate">
        {task.priority === '1' && <Icon i={Star} size={14} className="mr-0.5 inline align-[-0.2em] fill-current text-warn" />}
        {task.blockedByCount > 0 && <Icon i={Ban} size={14} className="mr-0.5 inline align-[-0.2em] text-danger" />}
        {task.name}
      </span>
    </button>
  );
}

function DayCell({
  day, dateKey, isToday, tasks, composing, onCompose, onCreate, metaOf, colorOf,
}: {
  day: number; dateKey: string; isToday: boolean; tasks: TaskListItem[];
  composing: boolean; onCompose: (k: string | null) => void;
  /** Absent = this calendar cannot create (My calendar spans projects, so there
   *  is no one project a new task would belong to). The ＋ then never renders. */
  onCreate?: (dateKey: string, name: string) => Promise<boolean>;
  metaOf?: (t: TaskListItem) => string | null;
  colorOf?: (t: TaskListItem) => string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dateKey}`, data: { dateKey } });

  const shown = expanded ? tasks : tasks.slice(0, 4);

  const submit = async () => {
    const name = draft.trim();
    if (!name || busy || !onCreate) return;
    setBusy(true);
    const ok = await onCreate(dateKey, name);
    setBusy(false);
    if (ok) { setDraft(''); onCompose(null); }
  };

  return (
    <div
      ref={setNodeRef}
      className={`group/day min-h-[96px] border-b border-r border-line p-1 transition ${isOver ? 'bg-brand/10 ring-1 ring-inset ring-brand/40' : ''}`}
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <span className={`text-[11px] ${isToday ? 'font-semibold text-brand' : 'text-faint'}`}>{day}</span>
        {onCreate && (
          <button
            onClick={() => onCompose(composing ? null : dateKey)}
            className="hidden rounded px-1 text-faint hover:bg-hover hover:text-ink group-hover/day:block"
            title={`Add a task on ${dateKey}`}
          ><Icon i={Plus} size={14} /></button>
        )}
      </div>
      {composing && onCreate && (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void submit(); }
            else if (e.key === 'Escape') { setDraft(''); onCompose(null); }
          }}
          onBlur={() => { if (!draft.trim()) onCompose(null); }}
          placeholder={busy ? 'Adding…' : 'Task name…'}
          disabled={busy}
          className="mb-1 w-full rounded border border-brand bg-canvas px-1.5 py-0.5 text-[11px] text-ink outline-none placeholder:text-faint"
        />
      )}
      <div className="space-y-1">
        {shown.map((t) => (
          <Chip key={t.id} task={t} dateStr={dateKey} meta={metaOf?.(t) ?? null} color={colorOf?.(t) ?? null} />
        ))}
        {tasks.length > 4 && (
          <button onClick={() => setExpanded((v) => !v)} className="px-1 text-[10px] text-faint hover:text-ink">
            {expanded ? 'show less' : `+${tasks.length - 4} more`}
          </button>
        )}
      </div>
    </div>
  );
}

export function MonthGrid({
  tasks, dateOf, onReschedule, onCreate, metaOf, colorOf, autoJump, hint, empty,
}: {
  tasks: TaskListItem[];
  /** Which day this task is filed under — the caller owns that decision, because
   *  it differs per project (film date for content teams, deadline elsewhere). */
  dateOf: (t: TaskListItem) => string | null;
  onReschedule: (t: TaskListItem, dateKey: string) => void;
  onCreate?: (dateKey: string, name: string) => Promise<boolean>;
  metaOf?: (t: TaskListItem) => string | null;
  /** Present = colour means WHICH PROJECT (dot), not urgency. */
  colorOf?: (t: TaskListItem) => string | null;
  /** Land on a month that has work instead of an empty current month. Right for MY
   *  calendar (a backlog); wrong for a project board, which should stay on today. */
  autoJump?: boolean;
  hint?: string;
  /** Shown over the grid when nothing at all is dated — an empty month of squares
   *  otherwise reads as "the calendar is broken" rather than "nothing scheduled". */
  empty?: string;
}) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  // Jump ONCE, on mount, to a month that actually has something — and say so.
  //
  // Opening on today is the right default for a project calendar, where work is
  // scheduled ahead. It is the wrong default for MY calendar over a real backlog:
  // Allyson has 400 open tasks, 131 of them dated, and NOT ONE in the current month —
  // her dated work is January to March. So the feature worked perfectly and looked
  // completely broken, which is worse than failing.
  const [jumpedTo, setJumpedTo] = useState<string | null>(null);
  const didJump = useRef(false);
  const [composeKey, setComposeKey] = useState<string | null>(null);
  const [activeTask, setActiveTask] = useState<TaskListItem | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  const byDate = useMemo(() => {
    const m = new Map<string, TaskListItem[]>();
    for (const t of tasks) {
      const d = dateOf(t);
      if (!d) continue;
      const key = d.slice(0, 10);
      const arr = m.get(key);
      if (arr) arr.push(t);
      else m.set(key, [t]);
    }
    return m;
  }, [tasks, dateOf]);

  // Nearest month with work, preferring the PAST on a tie: an overdue backlog is the
  // thing you need to see, and "nothing here" a month before your late work is a
  // worse landing spot than the late work itself.
  useEffect(() => {
    if (didJump.current || !autoJump || !byDate.size) return;
    didJump.current = true;
    const here = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}`;
    const months = [...new Set([...byDate.keys()].map((k) => k.slice(0, 7)))];
    if (months.includes(here)) return;
    const target = months
      .map((m) => ({ m, dist: Math.abs(new Date(`${m}-01T00:00:00`).getTime() - new Date(`${here}-01T00:00:00`).getTime()) }))
      .sort((a, b) => a.dist - b.dist || (a.m < b.m ? -1 : 1))[0];
    if (!target) return;
    const [y, mo] = target.m.split('-');
    setCursor({ year: Number(y), month: Number(mo) - 1 });
    setJumpedTo(new Date(`${target.m}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }));
  }, [autoJump, byDate, cursor.year, cursor.month]);

  const { year, month } = cursor;
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const now = new Date();
  const todayKey = ymd(now.getFullYear(), now.getMonth(), now.getDate());

  const shift = (delta: number) => {
    setCursor((c) => {
      const d = new Date(c.year, c.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const onDragStart = (e: DragStartEvent) => setActiveTask((e.active.data.current?.task as TaskListItem) ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setActiveTask(null);
    const task = e.active.data.current?.task as TaskListItem | undefined;
    const dateKey = (e.over?.data.current as { dateKey?: string } | undefined)?.dateKey;
    if (!task || !dateKey) return;
    if (dateOf(task)?.slice(0, 10) === dateKey) return; // dropped where it already was
    onReschedule(task, dateKey);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="rounded px-2 py-1 text-sm text-muted hover:bg-hover">‹</button>
          <span className="min-w-[140px] text-center text-sm font-medium text-ink">{monthLabel}</span>
          <button onClick={() => shift(1)} className="rounded px-2 py-1 text-sm text-muted hover:bg-hover">›</button>
          <button
            onClick={() => setCursor({ year: new Date().getFullYear(), month: new Date().getMonth() })}
            className="ml-1 rounded border border-line px-2 py-1 text-xs text-muted hover:bg-hover"
          >
            Today
          </button>
        </div>
        <span className="text-xs text-faint">
          {hint ?? `${byDate.size} dated · drag to reschedule${onCreate ? ' · hover a day to add' : ''}`}
        </span>
      </div>

      {jumpedTo && (
        <p className="px-4 pb-1 text-xs text-warn">
          Nothing was dated in the current month — showing {jumpedTo}, the nearest month with your work.
        </p>
      )}
      {empty && byDate.size === 0 && (
        <p className="px-4 pb-1 text-xs text-faint">{empty}</p>
      )}

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="grid min-h-0 flex-1 grid-rows-[auto_1fr] px-4 pb-4">
          <div className="grid grid-cols-7">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-faint">{d}</div>
            ))}
          </div>
          <div className="grid min-h-0 grid-cols-7 overflow-y-auto rounded-lg border border-line">
            {cells.map((day, i) => {
              if (!day) return <div key={i} className="min-h-[96px] border-b border-r border-line bg-(--color-page-dim) p-1" />;
              const key = ymd(year, month, day);
              return (
                <DayCell
                  key={i}
                  day={day}
                  dateKey={key}
                  isToday={key === todayKey}
                  tasks={byDate.get(key) ?? []}
                  composing={composeKey === key}
                  onCompose={setComposeKey}
                  onCreate={onCreate}
                  metaOf={metaOf}
                  colorOf={colorOf}
                />
              );
            })}
          </div>
        </div>
        <DragOverlay dropAnimation={null}>
          {activeTask ? (
            <div className="max-w-[180px] truncate rounded bg-brand-soft px-1.5 py-0.5 text-[11px] text-ink shadow-lg">
              {activeTask.name}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
